import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ServicesAdd from "./ServicesAdd";
import ServicesEdit from "./ServicesEdit";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const ServicesList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const debounceRef = useRef(null);

  // Fetch list from server
  const fetchServices = async (q = "") => {
    setLoading(true);
    try {
      const url = q ? `service-offerings/?search=${encodeURIComponent(q)}` : "service-offerings/";
      const res = await ApiServices.get(url);
      const data = res?.data ?? res;
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      let msg = "Failed to fetch services.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchServices(search);
    }, 450);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // handlers
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (service) => {
    setSelectedService(service);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setSelectedService(null);
    setOpenEdit(false);
  };

  const handleDeleteClick = (service) => {
    setDeleteTarget(service);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setConfirmOpen(false);
    setLoading(true);
    try {
      await ApiServices.delete(`service-offerings/${deleteTarget.id}/`);
      setSnack({ open: true, message: "Service deleted", severity: "success" });
      await fetchServices(search);
    } catch (err) {
      console.error(err);
      let msg = "Delete failed.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  // callbacks from add/edit components
  const onAddSuccess = async (msg = "Service added") => {
    setSnack({ open: true, message: msg, severity: "success" });
    setOpenAdd(false);
    await fetchServices(search);
  };

  const onEditSuccess = async (msg = "Service updated") => {
    setSnack({ open: true, message: msg, severity: "success" });
    handleCloseEdit();
    await fetchServices(search);
  };

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  return (
    <Box sx={containerStyles}>
      {/* small CSS tweaks kept in-file */}
      <style>{`
        .services-header {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          margin-bottom:16px;
        }
        .search-field { max-width:360px; width:100%; }
        .full-width-btn { min-width:160px; }
      `}</style>

      <Box className="services-header">
        <Typography variant="h5" sx={{ color: "#094c9f", fontWeight: 700 }}>
          Services
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            className="search-field"
            placeholder="Search services..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            className="full-width-btn"
            sx={{ backgroundColor: "#094c9f", "&:hover": { backgroundColor: "#083b82" } }}
          >
            Add New Service
          </Button>
        </Box>
      </Box>

      {/* Paper + TableContainer with overflow handling */}
      <Paper sx={{ width: "100%", overflow: "hidden" }} elevation={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: "60vh",        /* vertical scroll if table grows */
              overflowX: "auto",        /* horizontal scroll instead of page overflow */
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: 700,
                tableLayout: "fixed", /* prevent long cells from expanding the table */
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: "60px" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "180px" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "42%" }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "110px" }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "90px" }}>Active</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: "120px" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {services.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No services found.
                    </TableCell>
                  </TableRow>
                ) : (
                  services.map((s, idx) => (
                    <TableRow key={s.id} hover>
                      <TableCell sx={{ width: "60px" }}>{idx + 1}</TableCell>

                      <TableCell sx={{ width: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.name}
                      </TableCell>

                      <TableCell
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          maxWidth: { xs: "160px", sm: "320px", md: "520px" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.description}
                      </TableCell>

                      <TableCell sx={{ width: "110px" }}>
                        {typeof s.price === "string" ? parseFloat(s.price).toFixed(2) : s.price}
                      </TableCell>

                      <TableCell sx={{ width: "90px" }}>{s.is_active ? "Yes" : "No"}</TableCell>

                      <TableCell align="right" sx={{ width: "120px" }}>
                        <IconButton onClick={() => handleOpenEdit(s)} aria-label="edit" size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(s)} aria-label="delete" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add dialog */}
      <Dialog open={openAdd} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Service</DialogTitle>
        <DialogContent>
          <ServicesAdd onSuccess={onAddSuccess} onCancel={handleCloseAdd} />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent>
          {selectedService && (
            <ServicesEdit service={selectedService} onSuccess={onEditSuccess} onCancel={handleCloseEdit} />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServicesList;

const containerStyles = {
  p: 3,
  width: "100%",
  minHeight: "70vh",
  background: "#fff",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};
